/**
 * Taking a slide off the screen: as a PNG file, or full screen to present.
 *
 * Shared by the status report and the DDI portfolio, so both hand out the same
 * image: the slide at its native 1280x720 rather than the scaled-down copy on
 * the page, at twice the size so the text stays sharp in PowerPoint.
 */

import { useEffect, useRef, useState } from 'react'
import { X } from 'lucide-react'
import { toPng } from 'html-to-image'
import { SLIDE_W, SLIDE_H } from './ReportSlide'

/** Windows and macOS both refuse these in a filename. */
const safeName = (name: string) => name.replace(/[\\/:*?"<>|]+/g, ' ').replace(/\s+/g, ' ').trim()

/**
 * Put `ref` on a div wrapping the slide at its native size, then call
 * `download('Name of the file')` to save it as a PNG.
 */
export function useSlidePng() {
  const ref = useRef<HTMLDivElement>(null)
  const [exporting, setExporting] = useState(false)
  const [error, setError] = useState('')

  const download = async (filename: string) => {
    if (!ref.current) return
    setExporting(true)
    setError('')
    try {
      const url = await toPng(ref.current, { width: SLIDE_W, height: SLIDE_H, pixelRatio: 2, cacheBust: true })
      const link = document.createElement('a')
      link.download = `${safeName(filename)}.png`
      link.href = url
      link.click()
    } catch {
      setError("Couldn't create the PNG. Try again.")
    } finally {
      setExporting(false)
    }
  }

  return { ref, download, exporting, error }
}

/**
 * The slide filling the window, over the app: the largest it can be drawn
 * without leaving TrackIT. Deliberately not the browser's own full screen,
 * which hides the tabs and toolbar and has to be dismissed before anything
 * else can be clicked. Esc or the Close button puts it away.
 */
export function FullScreenSlide({ children, onClose }: { children: React.ReactNode; onClose: () => void }) {
  const fit = () => Math.min(window.innerWidth / SLIDE_W, window.innerHeight / SLIDE_H)
  const [scale, setScale] = useState(fit)
  // Held in a ref so a parent re-render (a new closure) never re-runs the effect.
  const close = useRef(onClose)
  close.current = onClose

  useEffect(() => {
    const resize = () => setScale(fit())
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') close.current() }
    window.addEventListener('resize', resize)
    window.addEventListener('keydown', esc)
    // The page behind must not scroll while the overlay is up.
    const scrollbehind = document.body.style.overflow
    document.body.style.overflow = 'hidden'
    return () => {
      window.removeEventListener('resize', resize)
      window.removeEventListener('keydown', esc)
      document.body.style.overflow = scrollbehind
    }
  }, [])

  return (
    <div className="fixed inset-0 z-50 bg-slate-900 flex items-center justify-center group"
         role="dialog" aria-modal="true" aria-label="Slide, full screen">
      <div style={{ width: SLIDE_W * scale, height: SLIDE_H * scale }}>
        <div style={{ width: SLIDE_W, height: SLIDE_H, transform: `scale(${scale})`, transformOrigin: 'top left' }}>
          {children}
        </div>
      </div>
      <button onClick={onClose} autoFocus
              className="absolute top-4 right-4 flex items-center gap-1.5 px-3 py-1.5 rounded-lg bg-black/60 text-white/80 hover:text-white text-sm font-semibold opacity-0 group-hover:opacity-100 focus:opacity-100 transition-opacity">
        <X className="w-4 h-4" /> Close
      </button>
    </div>
  )
}
